import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Chip, LinearProgress, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { fetchData } from "../../BaseUrl";
import { printDocument, field, row, section, schoolHeader } from "../../utils/printUtils";

const fallbackSubjects = [
  { subject: "Tamil Reading", max: 30, marks: 26, classAvg: 24, grade: "A", status: "Good" },
  { subject: "Tamil Writing", max: 30, marks: 28, classAvg: 25, grade: "A+", status: "Excellent" },
  { subject: "Tamil Grammar", max: 20, marks: 15, classAvg: 14, grade: "B", status: "Good" },
  { subject: "Tamil Speaking", max: 20, marks: 18, classAvg: 16, grade: "A", status: "Excellent" },
  { subject: "Homework", max: 20, marks: 14, classAvg: 15, grade: "B", status: "Good" },
];

const statusStyle = (status) => {
  if (status === "Excellent") return { backgroundColor: "#e3f2fd", color: "#1976d2" };
  if (status === "Good") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  return { backgroundColor: "#ffebee", color: "#f44336" };
};

const gradeStyle = (grade) => {
  if (grade === "A+") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  if (grade === "A") return { backgroundColor: "#e3f2fd", color: "#1976d2" };
  if (grade === "B") return { backgroundColor: "#fff3e0", color: "#f57c00" };
  return { backgroundColor: "#ffebee", color: "#f44336" };
};

export default function ProgressReportDetail({ open, onClose, student }) {
  const [activeTerm, setActiveTerm] = useState(1);
  const [subjects, setSubjects] = useState([]);
  const [teacherComments, setTeacherComments] = useState("");

  const loadDetail = useCallback(async (term) => {
    if (!student) return;
    try {
      const appId = student.applicationId || sessionStorage.getItem("applicationId");
      const response = await fetchData(`teacher/progress/student/${appId}?term=${term}`);
      if (response?.data?.subjects) {
        setSubjects(response.data.subjects);
      } else {
        setSubjects(fallbackSubjects);
      }
    } catch (_) {
      setSubjects(fallbackSubjects);
    }
  }, [student]);

  useEffect(() => {
    if (open) loadDetail(activeTerm);
  }, [open, loadDetail, activeTerm]);

  if (!open || !student) return null;

  const overallPct = student.overall || 85;
  const grade = student.grade || "A";
  const attendance = student.attendance || 9;

  const handlePrint = () => {
    printDocument(`Progress Report — ${student?.studentName}`, `
      ${schoolHeader('Student Progress Report', 'Academic Year 2026–2027')}
      ${section('Student Details', `
        ${row(field('Name', student?.studentName), field('Grade', student?.grade))}
        ${row(field('Registration No', student?.regNo || student?.studentId), field('Overall %', `${overallPct}%`))}
        ${row(field('Grade', grade), field('Attendance', `${attendance * 10}%`))}
      `)}
      ${section(`Term ${activeTerm} — Assessment Results`, `
        <table>
          <tr>
            <th>Subject</th>
            <th>Total Marks</th>
            <th>Scored</th>
            <th>Class Avg</th>
            <th>% Score</th>
            <th>Grade</th>
            <th>Status</th>
          </tr>
          ${subjects.map(s => `
            <tr>
              <td>${s.subject || '—'}</td>
              <td>${s.max || '—'}</td>
              <td>${s.marks || '—'}</td>
              <td>${s.classAvg || '—'}</td>
              <td><strong>${s.max ? Math.round((s.marks / s.max) * 100) + '%' : '—'}</strong></td>
              <td>${s.grade || '—'}</td>
              <td>${s.status || '—'}</td>
            </tr>
          `).join('')}
        </table>
      `)}
      ${teacherComments ? section("Teacher's Comments", `<p>${teacherComments}</p>`) : ''}
    `);
  };

  return (
    <Box>
      <Box sx={{ background: "linear-gradient(135deg,#4A7BA8,#244C77)", p: "24px", color: "#fff", borderRadius: "4px 4px 0 0" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: "60px", height: "60px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", fontWeight: "bold" }}>
              {(student.studentName || "S").charAt(0)}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>{student.studentName}</Typography>
              <Typography sx={{ fontSize: "13px", opacity: 0.85 }}>{student.grade || "Grade 3"} | Section A</Typography>
              <Box sx={{ display: "flex", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                {student.studentId && (
                  <Typography sx={{ fontSize: "12px", opacity: 0.85 }}>ID: {student.studentId}</Typography>
                )}
                {student.regNo && (
                  <Typography sx={{ fontSize: "12px", opacity: 0.85 }}>Reg: {student.regNo}</Typography>
                )}
                {student.dob && (
                  <Typography sx={{ fontSize: "12px", opacity: 0.85 }}>DOB: {student.dob}</Typography>
                )}
                {student.gender && (
                  <Typography sx={{ fontSize: "12px", opacity: 0.85 }}>Gender: {student.gender}</Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)", textTransform: "none", "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.1)" } }}
            >
              Print / PDF
            </Button>
            <IconButton onClick={onClose} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
          <Box sx={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "8px", px: 2, py: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>{overallPct}%</Typography>
            <Typography sx={{ fontSize: "11px", opacity: 0.85 }}>Overall</Typography>
          </Box>
          <Box sx={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "8px", px: 2, py: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>{grade}</Typography>
            <Typography sx={{ fontSize: "11px", opacity: 0.85 }}>Grade</Typography>
          </Box>
          <Box sx={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "8px", px: 2, py: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>{attendance * 10}%</Typography>
            <Typography sx={{ fontSize: "11px", opacity: 0.85 }}>Attendance</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: "20px" }}>
        <Box sx={{ display: "flex", gap: 0, mb: 2, border: "1px solid #D5D7DA", borderRadius: "8px", overflow: "hidden", width: "fit-content" }}>
          {[1, 2, 3].map((t) => (
            <Button
              key={t}
              onClick={() => setActiveTerm(t)}
              sx={{
                textTransform: "none", fontSize: "13px", borderRadius: 0, px: 3,
                backgroundColor: activeTerm === t ? "#244C77" : "transparent",
                color: activeTerm === t ? "#fff" : "#8F95B2",
                "&:hover": { backgroundColor: activeTerm === t ? "#244C77" : "#f0f0f0" },
              }}
            >
              Term {t}
            </Button>
          ))}
        </Box>

        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#374151", mb: 1 }}>Term {activeTerm} — Subject Details</Typography>

        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 8px" }}>
            <thead>
              <tr style={{ backgroundColor: "#8F95B2" }}>
                {["Category", "Total Marks", "Scored", "Class AVG", "% Score", "Bar", "Grade", "Status"].map((h, i) => (
                  <th key={i} style={{ color: "#fff", padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "13px",
                    borderRadius: i === 0 ? "8px 0 0 0" : i === 7 ? "0 8px 0 0" : "0" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subjects.map((row, idx) => {
                const pct = Math.round((row.marks / row.max) * 100);
                return (
                  <tr key={idx} style={{ backgroundColor: "#fff" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f9f9ff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fff"; }}>
                    <td style={{ padding: "12px", fontSize: "13px", fontWeight: "600", color: "#333" }}>{row.subject}</td>
                    <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.max}</td>
                    <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.marks}</td>
                    <td style={{ padding: "12px", color: "#8F95B2", fontSize: "13px" }}>{row.classAvg}</td>
                    <td style={{ padding: "12px", color: "#333", fontSize: "13px", fontWeight: "bold" }}>{pct}%</td>
                    <td style={{ padding: "12px", minWidth: "120px" }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{ borderRadius: "4px", height: "8px", backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#4A7BA8,#244C77)" } }}
                      />
                    </td>
                    <td style={{ padding: "12px" }}>
                      <Chip label={row.grade} size="small" sx={{ ...gradeStyle(row.grade), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
                    </td>
                    <td style={{ padding: "12px" }}>
                      <Chip label={row.status} size="small" sx={{ ...statusStyle(row.status), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        {/* Teacher Comments */}
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#374151", mb: 1 }}>Teacher's Comments:</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={teacherComments}
            onChange={(e) => setTeacherComments(e.target.value)}
            placeholder="Enter comments or observations about the student's progress..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "& fieldset": { borderColor: "#D5D7DA" },
              },
              fontSize: "13px",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
